import { arrayOfAll } from '@gitbook/util-ts';

import {
    type Revision,
    type RevisionPageDocument,
    type RevisionPageGroup,
    type RevisionPageHideable,
    RevisionPageType,
} from '../../types';
import { mutations } from '../../utils';
import { getPageParentInRevision } from './getPageParentInRevision';
import { isRevisionPageLink } from './guards';
import { updatePageInRevision } from './updatePageInRevision';

const visibilityKeys = arrayOfAll<keyof RevisionPageHideable>()([
    'hidden',
    'noIndex',
    'noRobotsIndex',
]);

export type PageVisibilityAggregated = {
    [Key in keyof RevisionPageHideable]-?: {
        value: boolean;
        /** If true, the value is enforced by an ancestor page */
        setFromAncestor: boolean;
        /** If true, the value is enforced by another property */
        setFromOther: boolean;
    };
};

const pageVisibilityCache = new WeakMap<Revision, Map<string, PageVisibilityAggregated>>();

/**
 * Get a cached version of the visibility state of a page.
 * This is a performant version of `getPageVisibilityInRevision`
 * that should be used in cases where the revision is not expected to mutate.
 */
export function getCachedPageVisibilityInRevision(
    revision: Revision,
    pageId: string
): PageVisibilityAggregated {
    let cache = pageVisibilityCache.get(revision);
    if (!cache) {
        cache = new Map();
        pageVisibilityCache.set(revision, cache);
    }

    let visibility = cache.get(pageId);
    if (!visibility) {
        const parent = getPageParentInRevision(revision, pageId);
        visibility = getPageVisibilityInRevision(
            revision,
            pageId,
            parent ? getCachedPageVisibilityInRevision(revision, parent.id) : null
        );
        cache.set(pageId, visibility);
    }

    return visibility;
}

/**
 * Return the visibility state of a page, based on its own hidden state and the hidden state of its ancestors.
 */
export function getPageVisibilityInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    parentVisibility: PageVisibilityAggregated | null = getPageParentVisibilityInRevision(
        revision,
        pageId
    )
): PageVisibilityAggregated {
    const page = mutations.getIn(revision, ['pages', pageId] as const);
    if (!page) {
        throw new Error(`Page ${pageId} not found in revision`);
    }

    const pageHiddeable = isRevisionPageLink(page)
        ? null
        : (page as mutations.MutableData<RevisionPageHideable>);

    const result = Object.fromEntries(
        visibilityKeys.map((key) => {
            const ancestorKeyState = parentVisibility ? parentVisibility[key] : null;
            if (ancestorKeyState?.value) {
                return [
                    key,
                    {
                        value: true,
                        setFromAncestor: true,
                        setFromOther: false,
                    },
                ];
            }

            return [
                key,
                {
                    value: pageHiddeable ? mutations.get(pageHiddeable, key) === true : false,
                    setFromAncestor: false,
                    setFromOther: false,
                },
            ];
        })
    ) as PageVisibilityAggregated;

    // Special enforcement rules
    if (result.noIndex.value) {
        result.noRobotsIndex = {
            value: true,
            setFromAncestor: false,
            setFromOther: true,
        };
    }

    return result;
}

/**
 * Get the visibility aggregated state of the parent of a page.
 */
export function getPageParentVisibilityInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string
): PageVisibilityAggregated | null {
    const parent = getPageParentInRevision(revision, pageId);
    if (!parent) {
        return null;
    }

    return getPageVisibilityInRevision(revision, mutations.get(parent, 'id'));
}

/**
 * Update the visibility state of a page.
 */
export function updatePageVisibilityInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    state: RevisionPageHideable
): void {
    updatePageInRevision(revision, pageId, (page) => {
        const type = mutations.get(page, 'type');
        if (type === RevisionPageType.Link) {
            return;
        }

        visibilityKeys.forEach((key) => {
            if (state[key] === undefined) {
                return;
            }

            mutations.set(
                page as mutations.MutableData<RevisionPageDocument | RevisionPageGroup>,
                key,
                state[key]
            );
        });
    });
}
